import { liveKeyOfAddress } from "@common/navigation/liveAddress"
import { liveModeConstants } from "@data/constants/liveModeConstants"
import moment from "moment"
import { REHYDRATE } from "redux-persist"

/**
 * La mémoire du live : le live où cet appareil est entré (`isLiveModeEnabled`, `liveSessionId`),
 * le jour où il y est entré (`enteredOn`) et la clé de cette entrée (`entryKey`), qui fait son
 * adresse au web.
 *
 * Elle est persistée pour qu'un rechargement en plein live y revienne, et ne s'efface qu'à la
 * sortie du live. Un onglet fermé ou une app tuée pendant le live, sur l'écran de fin ou sur le
 * bilan, y renvoyait donc au démarrage suivant — le lendemain compris : le serveur refusait
 * (« accessible uniquement le jour-même ») et l'élève cliquait « Ok » pour arriver à l'accueil.
 *
 * Au démarrage, elle ne rouvre donc que le live entré aujourd'hui ; au web, seulement à son
 * adresse (`/live/<clé>`, `liveAddress`), si bien que `/`, un second onglet et une adresse forgée
 * mènent à l'accueil.
 * Un live qu'elle ne rouvre pas est tenu pour quitté, comme à la sortie.
 */

/** Le jour local d'une date, `AAAA-MM-JJ` : celui de l'appareil, qui a daté l'entrée. */
export const localDay = (date) => moment(date).format("YYYY-MM-DD")

/**
 * La clé d'une nouvelle entrée dans le live : opaque, jamais l'id de la séance. Elle n'est relue que
 * par cet appareil, contre sa mémoire ; le serveur garde seul le contrôle d'accès au live.
 */
export const newLiveEntryKey = () =>
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10)

/** La clé du live ouvert dans cet onglet (son adresse au web), ou null. */
export function openLiveKey(state) {
    const { isLiveModeEnabled, entryKey } = state?.liveMode ?? {}
    return isLiveModeEnabled && entryKey ? entryKey : null
}

/** Le live ouvert dans cet onglet, ou null. */
export function openLiveSessionId(state) {
    const { isLiveModeEnabled, liveSessionId } = state?.liveMode ?? {}
    return isLiveModeEnabled && Number.isFinite(liveSessionId)
        ? liveSessionId
        : null
}

/** La mémoire rouvre-t-elle ce live au démarrage ? `address` : `null` dans l'app, sans adresse. */
function reopensOnBoot(liveMode, address) {
    const liveSessionId = openLiveSessionId({ liveMode })
    if (liveSessionId == null) return false
    if (liveMode.enteredOn !== localDay(new Date())) return false
    return address == null || liveKeyOfAddress(address) === liveMode.entryKey
}

/**
 * Le réducteur persisté, qui quitte au démarrage le live que la mémoire ne rouvre pas, comme à la
 * sortie (`RESET_LIVE_MODE`, que d'autres tranches écoutent aussi). C'est fait dans la
 * réhydratation même, et pas après : quand `bootstrapped` passe à vrai, tout ce qui lit l'état
 * (`PersistGate`, la mise à jour au lancement) lit déjà le live tel que la règle le laisse.
 * Seule la première réhydratation compte : un onglet qui adopte la connexion d'un autre onglet
 * (`crossTabSession`) se réhydrate encore, sans redémarrer.
 * `bootAddress` : le chemin de la page chargée, au web ; `null` dans l'app.
 */
export function withLiveMemoryOnBoot(
    persistedReducer,
    persistKey,
    bootAddress
) {
    return (state, action) => {
        const next = persistedReducer(state, action)
        if (action.type !== REHYDRATE || action.key !== persistKey) return next
        if (state?._persist?.rehydrated || !next?.liveMode?.isLiveModeEnabled)
            return next
        if (reopensOnBoot(next.liveMode, bootAddress)) return next
        return persistedReducer(next, {
            type: liveModeConstants.RESET_LIVE_MODE,
        })
    }
}
